import { useCallback, useState } from "react";
import { ServerInferRequest } from "@ts-rest/core";
import { counterpartyContract } from "@common/contracts/counterparty.contract";
import { Counterparty } from "@common/types/counterparty";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useCopyGlobalCounterpartyToLocalMutation,
    useUpdateCounterpartyMutation,
} from "@api/counterparty.api";

type CounterpartyUpdate = ServerInferRequest<
    typeof counterpartyContract.updateCounterparty
>["body"];

interface UseUpdateCounterpartyFieldMutationParams {
    counterparty: Counterparty;
    errorMessage: string;
}

export function useUpdateCounterpartyFieldMutation({
    counterparty,
    errorMessage,
}: UseUpdateCounterpartyFieldMutationParams) {
    const [isLoading, setIsLoading] = useState(false);
    const toaster = useToaster();

    const { mutateAsync: updateCounterparty } =
        useUpdateCounterpartyMutation(counterparty);
    const { mutateAsync: copyGlobalToLocal } =
        useCopyGlobalCounterpartyToLocalMutation();

    const updateField = useCallback(
        async (update: CounterpartyUpdate): Promise<boolean> => {
            setIsLoading(true);
            try {
                if (counterparty.workspaceId) {
                    await updateCounterparty(update);
                } else {
                    await copyGlobalToLocal({
                        globalCounterpartyId: counterparty.id,
                        update,
                    });
                }
                return true;
            } catch (error) {
                if (!isViewOnlyRefusalError(error)) {
                    toaster.error(errorMessage);
                }
                return false;
            } finally {
                setIsLoading(false);
            }
        },
        [
            counterparty,
            updateCounterparty,
            copyGlobalToLocal,
            toaster,
            errorMessage,
        ],
    );

    return { updateField, isLoading };
}
